import { useState } from "react";
import PmoShell from "./PmoShell";
import Markdown from "@/components/Markdown";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Sparkles, ScrollText } from "lucide-react";

export default function PmoExecSummary() {
  const [scope, setScope] = useState("active");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/portfolio/ai/executive-summary?scope=${scope}`);
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate the summary");
    } finally { setBusy(false); }
  };

  return (
    <PmoShell supervisorOnly
      title="AI Executive Summary"
      subtitle="A leadership-ready narrative across your whole portfolio — health verdict, highlights, projects needing attention, cross-portfolio risks and outlook."
      testid="pmo-exec-summary-page"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-52" data-testid="pmo-exec-scope-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active projects</SelectItem>
            <SelectItem value="all">All projects</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={generate} disabled={busy} className="gap-2" data-testid="pmo-exec-generate">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Writing summary…</> : <><Sparkles className="h-4 w-4" /> Generate summary</>}
        </Button>
      </div>
      {!result && !busy && (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="pmo-exec-empty">
          <ScrollText className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Pick a scope and generate — the AI reads progress, health, risks and late actions across every project. Takes ~15 seconds.</p>
        </div>
      )}
      {result && (
        <div className="rounded-2xl border border-border bg-white p-6" data-testid="pmo-exec-result">
          <Markdown text={result.summary} />
          {result.generated_at && (
            <p className="text-[11px] text-muted-foreground mt-4 border-t border-border pt-3">Generated {new Date(result.generated_at).toLocaleString()}</p>
          )}
        </div>
      )}
    </PmoShell>
  );
}
